import React, { useRef, useState } from 'react';
import {
    FileSpreadsheet, CheckCircle2, AlertTriangle,
    Loader2, TrendingUp, Target, RotateCcw, Users,
} from 'lucide-react';

// =====================================================================
// PAINEL ADMIN › AUTOMAÇÕES DE DADOS
// =====================================================================
// Sobe as planilhas de Projeção (dtbProjecao) e Orçamento (dtbOrcado) pela tela,
// substituindo os loaders CLI (node home/carga-*.js). Fluxo em 2 passos:
//   1) Validar  → POST ...?dry=1 : só lê e mostra o resumo (nada é escrito).
//   2) Confirmar e gravar → POST sem dry : upsert por PK (não apaga histórico).
// Só Admin chega aqui (a rota /admin/dashboard é Admin-only). Estilos inline
// seguindo a paleta do Painel Admin (slate).

// 'YYYY-MM-DD' → 'DD/MM/YYYY' (o backend devolve o período como data-só).
const fmtData = (iso) => {
    if (!iso) return '—';
    const [a, m, d] = iso.split('-');
    return `${d}/${m}/${a}`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString('pt-BR');

// --- Um card de carga (Projeção OU Orçamento) --------------------------------
// endpoint: rota base sem query (o ?dry=1 é adicionado no passo Validar).
function CardCarga({ titulo, descricao, colunas, endpoint, cor, bgCor, icone, notaGravacao }) {
    const inputRef = useRef(null);
    const [arquivo, setArquivo] = useState(null);
    const [estado, setEstado] = useState('idle'); // idle|validando|validado|gravando|gravado|erro
    const [resumo, setResumo] = useState(null);    // resultado do dry (linhas, válidas, período...)
    const [gravadas, setGravadas] = useState(0);
    const [erro, setErro] = useState(null);

    const reset = () => {
        setArquivo(null); setEstado('idle'); setResumo(null); setGravadas(0); setErro(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const escolher = (e) => {
        const f = e.target.files?.[0] || null;
        setArquivo(f); setEstado('idle'); setResumo(null); setErro(null);
    };

    // Envia a planilha. dry=true → só valida; dry=false → grava (upsert).
    const enviar = async (dry) => {
        if (!arquivo) return;
        setEstado(dry ? 'validando' : 'gravando');
        setErro(null);
        try {
            const fd = new FormData();
            fd.append('arquivo', arquivo);
            const resp = await fetch(`${endpoint}${dry ? '?dry=1' : ''}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                body: fd, // sem Content-Type: o browser põe o boundary do multipart
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Falha no envio.');
            if (dry) { setResumo(data); setEstado('validado'); }
            else { setGravadas(data.gravadas); setEstado('gravado'); }
        } catch (e) {
            setErro(e.message); setEstado('erro');
        }
    };

    const ocupado = estado === 'validando' || estado === 'gravando';

    return (
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Cabeçalho do card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ padding: '10px', borderRadius: '12px', backgroundColor: bgCor, color: cor }}>{icone}</div>
                <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{titulo}</h3>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>{descricao}</p>
                </div>
            </div>

            {/* Colunas esperadas (ajuda contra arquivo errado) */}
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                Colunas esperadas: <span style={{ fontFamily: 'monospace', color: '#475569' }}>{colunas.join(', ')}</span>
            </div>

            {/* Seletor de arquivo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <input ref={inputRef} type="file" accept=".xlsx" onChange={escolher} disabled={ocupado}
                    style={{ fontSize: '13px', color: '#475569' }} />
            </div>

            {/* Botão Validar (aparece quando há arquivo e ainda não validou) */}
            {(estado === 'idle' || estado === 'validando') && (
                <button onClick={() => enviar(true)} disabled={!arquivo || ocupado}
                    style={{ ...btn, backgroundColor: arquivo ? cor : '#cbd5e1', cursor: arquivo && !ocupado ? 'pointer' : 'not-allowed' }}>
                    {estado === 'validando' ? <><Loader2 size={16} className="spin" /> Validando…</> : <><FileSpreadsheet size={16} /> Validar planilha</>}
                </button>
            )}

            {/* Resumo do dry + confirmação */}
            {estado === 'validado' && resumo && (() => {
                // O resumo varia por planilha: as de série (Projeção/Orçamento) trazem
                // válidas/descartadas/período; a de snapshot (Quadro Ideal) traz combos/
                // lojas/totalIdeal. Renderiza só os campos presentes. "temDados" habilita
                // o Gravar (válidas OU combos > 0).
                const temDados = (resumo.validas ?? resumo.combos ?? 0) > 0;
                return (
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '13px', color: '#334155' }}>
                            <span>Linhas na planilha: <strong>{fmtNum(resumo.linhas)}</strong></span>
                            {resumo.validas != null && <span>Válidas: <strong style={{ color: '#16a34a' }}>{fmtNum(resumo.validas)}</strong></span>}
                            {resumo.descartadas != null && <span>Descartadas: <strong style={{ color: resumo.descartadas ? '#dc2626' : '#334155' }}>{fmtNum(resumo.descartadas)}</strong></span>}
                            {resumo.periodo && <span>Período: <strong>{fmtData(resumo.periodo.min)} → {fmtData(resumo.periodo.max)}</strong></span>}
                            {resumo.combos != null && <span>Pares loja×cargo: <strong style={{ color: '#16a34a' }}>{fmtNum(resumo.combos)}</strong></span>}
                            {resumo.lojas != null && <span>Lojas: <strong>{fmtNum(resumo.lojas)}</strong></span>}
                            {resumo.totalIdeal != null && <span>Total ideal: <strong>{fmtNum(resumo.totalIdeal)}</strong></span>}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{notaGravacao}</div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={() => enviar(false)} disabled={ocupado || !temDados}
                                style={{ ...btn, backgroundColor: temDados ? '#16a34a' : '#cbd5e1', cursor: temDados && !ocupado ? 'pointer' : 'not-allowed' }}>
                                {estado === 'gravando' ? <><Loader2 size={16} className="spin" /> Gravando…</> : <><CheckCircle2 size={16} /> Confirmar e gravar</>}
                            </button>
                            <button onClick={reset} disabled={ocupado} style={{ ...btn, backgroundColor: '#e2e8f0', color: '#475569' }}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Gravando (quando veio direto do resumo) */}
            {estado === 'gravando' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                    <Loader2 size={16} className="spin" /> Gravando na base…
                </div>
            )}

            {/* Sucesso */}
            {estado === 'gravado' && (
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#15803d', fontWeight: 700 }}>
                        <CheckCircle2 size={18} /> {fmtNum(gravadas)} linhas gravadas com sucesso.
                    </div>
                    <button onClick={reset} style={{ ...btn, backgroundColor: '#e2e8f0', color: '#475569', alignSelf: 'flex-start' }}>
                        <RotateCcw size={16} /> Nova carga
                    </button>
                </div>
            )}

            {/* Erro */}
            {estado === 'erro' && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#b91c1c', fontSize: '13px' }}>
                        <AlertTriangle size={18} style={{ flexShrink: 0 }} /> <span>{erro}</span>
                    </div>
                    <button onClick={() => setEstado(resumo ? 'validado' : 'idle')} style={{ ...btn, backgroundColor: '#e2e8f0', color: '#475569', alignSelf: 'flex-start' }}>
                        Tentar de novo
                    </button>
                </div>
            )}
        </div>
    );
}

const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '8px', border: 'none', borderRadius: '10px',
    padding: '10px 18px', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer',
};

export default function AdminAutomacoes() {
    return (
        <div>
            {/* keyframe do spinner (inline, sem CSS externo) */}
            <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin 1s linear infinite}`}</style>

            <div style={{ marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>Automações de Dados</h2>
                <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#64748b', maxWidth: '640px' }}>
                    Suba as planilhas de <strong>Projeção</strong> e <strong>Orçamento</strong> direto por aqui — elas alimentam
                    os cards do Dashboard de Operação da Home. Cada envio primeiro <strong>valida</strong> (mostra o resumo, sem
                    gravar) e só grava após a sua confirmação.
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
                <CardCarga
                    titulo="Projeção de Vendas"
                    descricao="Projeção diária por loja/segmento (dtbProjecao)."
                    colunas={['DATA', 'LOJA', 'DS_POS', 'VL_PROJECAO', 'GC_PROJECAO', 'CNPJ']}
                    endpoint="/api/home/automacoes/projecao"
                    cor="#990605" bgCor="#fef2f2"
                    icone={<TrendingUp size={22} />}
                    notaGravacao={<>Gravação por <strong>upsert</strong>: insere as datas novas e atualiza as existentes — <strong>não apaga</strong> o histórico.</>}
                />
                <CardCarga
                    titulo="Orçamento"
                    descricao="Orçamento diário por loja/segmento (dtbOrcado)."
                    colunas={['DATA', 'LOJA', 'DS_POS', 'VL_ORCADO', 'GC_ORCADO', 'CNPJ']}
                    endpoint="/api/home/automacoes/orcado"
                    cor="#b45309" bgCor="#fffbeb"
                    icone={<Target size={22} />}
                    notaGravacao={<>Gravação por <strong>upsert</strong>: insere as datas novas e atualiza as existentes — <strong>não apaga</strong> o histórico.</>}
                />
                <CardCarga
                    titulo="Quadro Ideal (RH)"
                    descricao="Lotação ideal por cargo/loja (dtbQuadroIdeal). Alimenta o Real × Ideal da Visão RH."
                    colunas={['LOJA', 'NOME_CARGO_COMPILADO', 'QTD']}
                    endpoint="/api/home/automacoes/quadro-ideal"
                    cor="#7c3aed" bgCor="#f5f3ff"
                    icone={<Users size={22} />}
                    notaGravacao={<>Gravação por <strong>substituição por loja</strong>: as lojas do arquivo são regravadas (some quem saiu do quadro); lojas fora do arquivo ficam intactas.</>}
                />
            </div>
        </div>
    );
}
